import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import { LoginByEmailResponse } from '~/types'

import { ApiError } from '@mangovoice/types'

interface UserCredentials {
  username: string
  password: string
}

/**This query returns a refetch function which, when provided with a username/password, will invoke the query
 * return the results of the password login.
 * This call will not be cached, will not be retried automatically, and will not be refetched automatically on window focus.
 */
export const useLoginWithPassword = (
  onQuerySuccess:
    | ((data: LoginByEmailResponse | ApiError) => void)
    | undefined = undefined,
  options: Record<string, unknown> = {},
) => {
  const mangoApi = useMangoApi()
  const [userCredentials, setUserCredentials] = useState<
    UserCredentials | undefined
  >()

  const refetch = (username: string, password: string) =>
    setUserCredentials({ username, password })

  const query = useQuery({
    queryKey: ['loginWithPassword'],
    queryFn: async () => {
      if (!userCredentials) return Promise.resolve(undefined)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const result = await mangoApi.loginByEmail({ ...userCredentials })
      const { ok, data } = result
      if (!ok) {
        return Promise.reject(data as ApiError)
      }

      const tokenData = data as LoginByEmailResponse

      onQuerySuccess?.(tokenData)
      return tokenData
    },
    ...options,
    enabled: false,
    refetchOnWindowFocus: false,
    gcTime: 0,
    retry: false,
  })

  useEffect(() => {
    if (userCredentials) {
      query.refetch()
    }
  }, [userCredentials])

  return {
    ...query,
    refetch: refetch,
  }
}
